import type { AssetCameraKey, SiteId } from "@visioni/a3s-schema";
import { ListOrder, ListTenantActivationsQueryParams } from "../../client-messages/activations";
import { dayIn, daysBefore, isTimeZone, wallClockEnd, wallClockStart } from "./wall-clock";

/** Which slice of a tenant's activations a list shows. All absent spans the tenant's whole history. */
export interface TenantActivationFilters {
  readonly site?: SiteId;
  readonly camera?: AssetCameraKey;
  /** Inclusive UTC instant, against the activation's `createdDateTime`. */
  readonly since?: string;
  /** Inclusive UTC instant, against the activation's `createdDateTime`. */
  readonly until?: string;
  /** Absent reads newest first. */
  readonly order?: ListOrder;
}

export type ParsedTenantActivationFilters =
  | { readonly ok: true; readonly filters: TenantActivationFilters }
  | { readonly ok: false; readonly problem: string };

/**
 * Filters as they appear in a page URL.
 *
 * `from` / `to` are wall clocks in `tz` — `YYYY-MM-DD` for a whole day, or
 * `YYYY-MM-DDTHH:mm` once a time narrows it. `tz` is an IANA zone, and the page
 * always writes the one it resolved into the URL, so a shared link means the
 * same instants to whoever opens it, wherever they are.
 */
export interface RawActivationFilters {
  readonly site?: string | undefined;
  readonly camera?: string | undefined;
  readonly from?: string | undefined;
  readonly to?: string | undefined;
  readonly tz: string;
  /** `asc` (oldest first) or `desc` (newest first); absent is newest first. */
  readonly order?: string | undefined;
}

const blank = (value: string | undefined): string | undefined => (value === "" ? undefined : value);

type ParsedOrder = { readonly ok: true; readonly order?: ListOrder } | { readonly ok: false; readonly problem: string };

function parseOrder(raw: string | undefined): ParsedOrder {
  const order = blank(raw);
  if (order === undefined) return { ok: true };
  const result = ListOrder.safeParse(order);
  return result.success ?
      { ok: true, order: result.data }
    : {
        ok: false,
        problem: `The order "${order}" in this link is neither oldest first (asc) nor newest first (desc).`,
      };
}

type TimeRange =
  | { readonly ok: true; readonly since: string | undefined; readonly until: string | undefined }
  | { readonly ok: false; readonly problem: string };

/**
 * The instants a wall-clock range covers in `timeZone`.
 *
 * Resolved here rather than by the API because only the page knows which zone
 * "Friday 02:00" means — the Lambda runs in UTC and the replica indexes UTC
 * instants. Same reasoning as the clip compilation form's camera-zone bounds.
 */
function parseTimeRange(from: string | undefined, to: string | undefined, timeZone: string): TimeRange {
  if (!isTimeZone(timeZone)) {
    return { ok: false, problem: `The timezone "${timeZone}" in this link is not a known timezone.` };
  }
  const since = from === undefined ? undefined : wallClockStart(from, timeZone);
  const until = to === undefined ? undefined : wallClockEnd(to, timeZone);
  if (from !== undefined && since === undefined) {
    return { ok: false, problem: `The start "${from}" in this link is not a valid date and time.` };
  }
  if (to !== undefined && until === undefined) {
    return { ok: false, problem: `The end "${to}" in this link is not a valid date and time.` };
  }
  if (since !== undefined && until !== undefined && since > until) {
    return { ok: false, problem: "The time range in this link ends before it starts." };
  }
  return { ok: true, since, until };
}

/**
 * Reads list filters from raw URL params.
 *
 * Validated with the endpoint's own query schema, so the page refuses exactly
 * what the API would refuse. A hand-edited or stale link that fails is reported
 * as a problem rather than dropped: dropping `camera` would silently widen the
 * list to the whole site, and sending it would come back as an empty list or a
 * 400 — both of which read as "no activations here".
 */
export function parseTenantActivationFilters(raw: RawActivationFilters): ParsedTenantActivationFilters {
  const site = blank(raw.site);
  const camera = blank(raw.camera);
  const from = blank(raw.from);
  const to = blank(raw.to);

  const window = parseTimeRange(from, to, raw.tz);
  if (!window.ok) return window;
  const { since, until } = window;
  const order = parseOrder(raw.order);
  if (!order.ok) return order;

  const result = ListTenantActivationsQueryParams.safeDecode({
    ...(site !== undefined && { site }),
    ...(camera !== undefined && { camera }),
  });

  if (!result.success) {
    const issue = result.error.issues[0];
    const field = issue?.path.join(".") ?? "filter";
    return {
      ok: false,
      problem: `The ${field} filter in this link is not valid: ${issue?.message ?? "unknown error"}`,
    };
  }

  return {
    ok: true,
    filters: {
      ...(result.data.site !== undefined && { site: result.data.site }),
      ...(result.data.camera !== undefined && { camera: result.data.camera }),
      ...(since !== undefined && { since }),
      ...(until !== undefined && { until }),
      ...(order.order !== undefined && { order: order.order }),
    },
  };
}

/**
 * Identity of a filtered list, for anything that must start over when the
 * filters change — a cursor is only meaningful to the query that issued it.
 */
export function activationFiltersKey(filters: {
  readonly site?: string;
  readonly camera?: string;
  readonly since?: string;
  readonly until?: string;
  readonly order?: ListOrder;
  readonly clientNo?: string;
}): string {
  return [filters.site, filters.camera, filters.since, filters.until, filters.order, filters.clientNo]
    .map((v) => v ?? "")
    .join("|");
}

/** How far back the fleet list reaches when the link names no start day. */
export const FLEET_DEFAULT_DAYS = 365;

/** Which slice of the fleet's activations a list shows. Always bounded below — see `ListFleetActivationsQueryParams`. */
export interface FleetActivationFilters {
  /** Inclusive instant, against the activation's `createdDateTime`. */
  readonly since: string;
  /** Inclusive instant; absent reads up to now. */
  readonly until?: string;
  /** Absent reads newest first. */
  readonly order?: ListOrder;
  /** The start of a client number — an asset id finds its clients. Absent spans every client. See `FleetClientNoParam`. */
  readonly clientNo?: string;
}

/** A fleet list's client filter as a link carries it: trimmed, and blank is none. */
export const fleetClientNo = (raw: string | undefined): string | undefined => blank(raw?.trim());

export type ParsedFleetActivationFilters =
  | { readonly ok: true; readonly filters: FleetActivationFilters }
  | { readonly ok: false; readonly problem: string };

/**
 * Reads fleet list filters — a time range, and the start of a client number —
 * from raw URL params.
 *
 * A link naming no start shows the {@link FLEET_DEFAULT_DAYS} days up to its
 * end, or up to `today` in the filter's timezone. The default is a day rather
 * than an instant so the query, and the cursor it issues, stays the same
 * across renders.
 */
export function parseFleetActivationFilters(
  raw: Pick<RawActivationFilters, "from" | "to" | "tz" | "order"> & { readonly clientNo?: string | undefined },
  today: Date,
): ParsedFleetActivationFilters {
  const to = blank(raw.to);
  const window = parseTimeRange(blank(raw.from), to, raw.tz);
  if (!window.ok) return window;
  const order = parseOrder(raw.order);
  if (!order.ok) return order;

  const from = blank(raw.from) ?? daysBefore(to ?? dayIn(today, raw.tz), FLEET_DEFAULT_DAYS - 1);
  // Only `undefined` for a defaulted day, which is always valid.
  const since = window.since ?? (from === undefined ? undefined : wallClockStart(from, raw.tz));
  if (from === undefined || since === undefined) return { ok: false, problem: "The start of this range is not valid." };
  const clientNo = fleetClientNo(raw.clientNo);

  return {
    ok: true,
    filters: {
      since,
      ...(window.until !== undefined && { until: window.until }),
      ...(order.order !== undefined && { order: order.order }),
      ...(clientNo !== undefined && { clientNo }),
    },
  };
}
